import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { SlTabGroup, SlTab, SlTabPanel } from '@shoelace-style/shoelace/dist/react';
import Datepicker from "react-tailwindcss-datepicker";
import RegionWiseReport from './RegionWiseReport';
import LocationDateWiseReport from './LocationDateWiseReport';
import LocationMonthWiseReport from './LocationMonthWiseReport';
import CategoryWiseReport from './CategoryWiseReport';
import SalesPersonWiseReport from './SalesPersonWiseReport';
import CreativeReportDesignerAssigned from './CreativeReportDesignerAssigned';
import Select from "react-select";
import API from '../../services/api';

function getAlphaDefaultPickerValue() {
  const end = new Date();
  end.setHours(0, 0, 0, 0);
  const start = new Date();
  start.setDate(1);
  start.setHours(0, 0, 0, 0);
  return { startDate: start, endDate: end };
}

function toDateRange(pickerValue) {
  if (!pickerValue?.startDate || !pickerValue?.endDate) return [null, null];
  const s = pickerValue.startDate instanceof Date ? new Date(pickerValue.startDate) : new Date(pickerValue.startDate);
  const e = pickerValue.endDate instanceof Date ? new Date(pickerValue.endDate) : new Date(pickerValue.endDate);
  s.setHours(0, 0, 0, 0);
  e.setHours(0, 0, 0, 0);
  return [s, e];
}

function AlphaReports() {
  const { tab } = useParams();
  const navigate = useNavigate();
  const [pickerValue, setPickerValue] = useState(() => getAlphaDefaultPickerValue());
  const dateRange = useMemo(() => toDateRange(pickerValue), [pickerValue]);
  const [activeTab, setActiveTab] = useState(tab || "region_wise_report");
  const [initiateDownloadCSV, setInitiateDownloadCSV] = useState(false);
  const [alphaSalesPersons, setAlphaSalesPersons] = useState([]);
  const [selectedSalesPersonId, setSelectedSalesPersonId] = useState(0);
  useEffect(() => {
    if (tab && tab !== activeTab) setActiveTab(tab);
  }, [tab]);

  const handleTabChange = (e) => {
    const name = e.detail.name;
    setActiveTab(name);
    navigate(`/home/alpha_reports/${name}`, { replace: true });
  };

  const getAlphaSalesPersons = async () => {
    const response = await API({
      url: "/alpha/get-alpha-sales-persons",
      method: "POST",
      responseType: "json",
    });
    if (response?.code === 200) {
      setAlphaSalesPersons(Array.isArray(response.data) ? response.data : []);
    }
  };

  useEffect(() => {
    getAlphaSalesPersons();
  }, []);

  return (
    <div className="p-2 font-sans">
      <div className="flex justify-between mb-2">
        {/* <label className="mb-1 font-semibold">Select Date Range *</label> */}
          <div className="flex gap-2">
        <div className="border rounded-lg border-gray-300 hover:border-2 hover:border-sky-500 max-w-md">
          <Datepicker
            classNames="form-input w-full"
            value={pickerValue}
            primaryColor="cyan"
            showShortcuts={true}
            configs={{
              shortcuts: {
                today: "today",
                yesterday: "yesterday",
                currentMonth: "currentMonth",
                pastMonth: "lastMonth",
              },
            }}
            maxDate={new Date()}
            onChange={(newValue) => setPickerValue(newValue ?? { startDate: null, endDate: null })}
            popoverDirection="down"
          />
          </div>
          <div>
            <Select className="z-10 w-64" options={[{label: "Select Sales Person", value: 0}, ...alphaSalesPersons]} value={alphaSalesPersons.find(salesPerson => salesPerson.value === selectedSalesPersonId) || null} onChange={(selected) => setSelectedSalesPersonId(selected?.value || 0)} placeholder="Select Sales Person" />
          </div>
        </div>
        <button
          type="button"
          className="btn btn-primary btn-sm"
          style={{ height: 30 }}
          onClick={() => setInitiateDownloadCSV(true)}
        >
            <i className="bi bi-download me-1" />
            Download CSV
          </button>
      </div>
      <SlTabGroup onSlTabShow={handleTabChange}>
        <SlTab slot="nav" panel="region_wise_report" active={activeTab === 'region_wise_report'}>Region Wise Report</SlTab>
        <SlTab slot="nav" panel="location_date_wise_report" active={activeTab === 'location_date_wise_report'}>Location Date Wise Report</SlTab>
        <SlTab slot="nav" panel="location_month_wise_report" active={activeTab === 'location_month_wise_report'}>Location Month Wise Report</SlTab>
        <SlTab slot="nav" panel="category_wise_report" active={activeTab === 'category_wise_report'}>Category Wise Report</SlTab>
        <SlTab slot="nav" panel="sales_person_wise_report" active={activeTab === 'sales_person_wise_report'}>Sales Person Wise Report</SlTab>
        <SlTab slot="nav" panel="alpha_creatives_report" active={activeTab === 'alpha_creatives_report'}>Alpha Creatives Report</SlTab>
        <SlTabPanel name="region_wise_report">
          {activeTab === "region_wise_report" && <RegionWiseReport key="region_wise_report" dateRange={dateRange} initiateDownloadCSV={initiateDownloadCSV} setInitiateDownloadCSV={setInitiateDownloadCSV} selectedSalesPersonId={selectedSalesPersonId} />}
        </SlTabPanel>
        <SlTabPanel name="location_date_wise_report">
          {activeTab === "location_date_wise_report" && <LocationDateWiseReport key="location_date_wise_report" dateRange={dateRange} initiateDownloadCSV={initiateDownloadCSV} setInitiateDownloadCSV={setInitiateDownloadCSV} selectedSalesPersonId={selectedSalesPersonId} />}
        </SlTabPanel>
        <SlTabPanel name="location_month_wise_report">
          {activeTab === "location_month_wise_report" && <LocationMonthWiseReport key="location_month_wise_report" dateRange={dateRange} initiateDownloadCSV={initiateDownloadCSV} setInitiateDownloadCSV={setInitiateDownloadCSV} selectedSalesPersonId={selectedSalesPersonId} />}
        </SlTabPanel>
        <SlTabPanel name="category_wise_report">
          {activeTab === "category_wise_report" && <CategoryWiseReport key="category_wise_report" dateRange={dateRange} initiateDownloadCSV={initiateDownloadCSV} setInitiateDownloadCSV={setInitiateDownloadCSV} selectedSalesPersonId={selectedSalesPersonId} />}
        </SlTabPanel>
        <SlTabPanel name="sales_person_wise_report">
          {activeTab === "sales_person_wise_report" && <SalesPersonWiseReport key="sales_person_wise_report" dateRange={dateRange} initiateDownloadCSV={initiateDownloadCSV} setInitiateDownloadCSV={setInitiateDownloadCSV} selectedSalesPersonId={selectedSalesPersonId} />}
        </SlTabPanel>
        <SlTabPanel name="alpha_creatives_report">
          {activeTab === "alpha_creatives_report" && <CreativeReportDesignerAssigned key="alpha_creatives_report" initiateDownloadCSV={initiateDownloadCSV} setInitiateDownloadCSV={setInitiateDownloadCSV} />}
        </SlTabPanel>
      </SlTabGroup>
      
    </div>
  );
}

export default AlphaReports